import React from 'react'
import styled from '@emotion/styled'
import {Hotel} from '@findhotel/sapi'
import {SearchOffer} from 'modules/sapiSearch/slice'

import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/src/components/designSystem/SkeletonLoader'
import {cssTheme} from '@vio/atlas/src/themes'
import {OfferVariant} from '@vio/core/src/offer/types/OfferVariant'

import {PriceComponent} from '../Primary/PriceComponent'
import {TagComponent} from '../Primary/TagComponent'
import {OfferLogo} from '../Shared/OfferLogo'
import {RoomLabelsComponent} from '../Shared/RoomLabelsComponent'
import {OtherSites} from './OtherSites'

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${cssTheme.layout.spacing.s400};

  .--long-price {
    .--text-xl {
      ${cssTheme.typography.titleL}
    }
  }
  .--anchor-price {
    ${cssTheme.typography.bodyS}
  }
`

interface Props {
  hotelId: Hotel['objectID']
  primaryOffer: SearchOffer
  cheapestOfferFromOthers?: SearchOffer
  offerVariant?: OfferVariant
  isYourChoice?: boolean
  sourceComponent?: string
  isLoading?: false
  /** When true, do not render OtherSites (OTA mode: no other-provider offers). */
  isOTAMode?: boolean
  otherOffersMetadata: {
    providerCode: string
    brandInfo: SearchOffer['metadata']['brandInfo']
  }[]
}

interface LoadingProps {
  isLoading: true
  hotelId?: never
  primaryOffer?: never
  cheapestOfferFromOthers?: never
  offerVariant?: never
  isYourChoice?: never
  sourceComponent?: never
  isOTAMode?: boolean
  otherOffersMetadata?: never
}

export const BoVioFirstOffer: React.FC<Props | LoadingProps> = ({
  isLoading,
  hotelId,
  primaryOffer,
  cheapestOfferFromOthers,
  offerVariant,
  sourceComponent,
  isOTAMode = false,
  otherOffersMetadata
}) => {
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent={isOTAMode ? 'flex-end' : 'space-between'}
        alignItems="flex-start"
      >
        {!isOTAMode && (
          <div>
            <SkeletonLoader width={63} />
            <SkeletonLoader width={111} />
          </div>
        )}
        <div>
          <SkeletonLoader width={63} align="right" />
          <SkeletonLoader width={111} align="right" />
        </div>
      </Box>
    )
  }

  return (
    <Wrapper data-id={sourceComponent}>
      <Box flex="1">
        {!isOTAMode && (
          <OfferLogo hotelId={hotelId} offer={primaryOffer} isPrimaryOffer />
        )}
        <Box marginTop="s100">
          <RoomLabelsComponent
            hasRoomLabels
            offer={primaryOffer}
            hotelId={hotelId}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gap="s100"
      >
        <TagComponent
          anchorPrice={cheapestOfferFromOthers?.nightlyRate}
          anchorPriceTotal={cheapestOfferFromOthers?.totalRate}
          offerVariant={offerVariant}
          offer={primaryOffer}
          variant="PrimaryMultiColumn"
        />
        <Box marginRight="s100" paddingRight="s100">
          <PriceComponent
            variant="PrimaryMultiColumn"
            shouldShowTaxationAndNightly
            hotelId={hotelId}
            anchorPrice={cheapestOfferFromOthers?.nightlyRate}
            anchorPriceTotal={cheapestOfferFromOthers?.totalRate}
            offer={primaryOffer}
            offerVariant={offerVariant}
          />
        </Box>
        {cheapestOfferFromOthers?.id && !isOTAMode && (
          <OtherSites
            hotelId={hotelId}
            otherOffersMetadata={otherOffersMetadata}
            cheapestOfferFromOthers={cheapestOfferFromOthers}
          />
        )}
      </Box>
    </Wrapper>
  )
}
